import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DropdownMenuItemWithTooltip } from '@/components/ui/DropdownMenuItemWithTooltip';
import {
  buildEcaGenerateBodyFromSelection,
  type EarlyCaseGenerateBody,
} from '@/hooks/useEarlyCaseAssessmentContent';
import type { FreshnessStatus } from '@/hooks/useMatterMemory';
import { Download, FileText, Menu, RefreshCw } from 'lucide-react';
import { EarlyCaseAssessmentDocumentSelectorDialog } from './EarlyCaseAssessmentDocumentSelectorDialog';

interface EarlyCaseAssessmentActionBarProps {
  slug?: string;
  savedSelection: string[];
  onSavedSelectionChange: (ids: string[]) => void;
  triggerGenerate: (body: EarlyCaseGenerateBody | undefined, options?: { regenerate?: boolean }) => Promise<void>;
  onDownload: () => Promise<void>;
  isDownloading: boolean;
  isDownloadDisabled: boolean;
  /** True while an assessment is generating; disables regeneration. */
  isGenerating: boolean;
  /** Fallback count for the picker's "auto-included" banner. */
  contributingDocumentCount: number | null;
  /** Drives the status label below the buttons. */
  freshnessStatus: FreshnessStatus | null;
  /** Effective document set (Client Documents + explicit picks) exceeds the per-matter cap. */
  overDocumentLimit: boolean;
  /** Hard cap on the effective document set; rendered in the warning copy. */
  documentLimit: number | null;
  /** Size of the effective document set; rendered in the warning copy. */
  effectiveDocumentCount: number | null;
}

export function EarlyCaseAssessmentActionBar({
  slug,
  savedSelection,
  onSavedSelectionChange,
  triggerGenerate,
  onDownload,
  isDownloading,
  isDownloadDisabled,
  isGenerating,
  contributingDocumentCount,
  freshnessStatus,
  overDocumentLimit,
  documentLimit,
  effectiveDocumentCount,
}: EarlyCaseAssessmentActionBarProps) {
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [isRegenerateConfirmOpen, setIsRegenerateConfirmOpen] = useState(false);
  // Only an explicit "Regenerate" reads as "Regenerating…"; an initial/additive
  // build must not, since it can surface partial markdown mid-run. We track the
  // user's intent at the moment a run starts rather than inferring it from
  // whether content already exists. Each handler that kicks off a run sets this,
  // so it always reflects the latest run.
  const [didRequestRegenerate, setDidRequestRegenerate] = useState(false);

  // Clear the regenerate intent once its run ends, so a later backend-driven run
  // doesn't inherit the "Regenerating…" label.
  useEffect(() => {
    if (!isGenerating && didRequestRegenerate) {
      setDidRequestRegenerate(false);
    }
  }, [isGenerating, didRequestRegenerate]);

  const handleSaveSelection = (cleaned: string[]) => {
    setDidRequestRegenerate(false);
    onSavedSelectionChange(cleaned);
    void triggerGenerate(buildEcaGenerateBodyFromSelection(cleaned));
  };

  const handleRegenerate = () => {
    setIsRegenerateConfirmOpen(false);
    setDidRequestRegenerate(true);
    // Omit the body so the backend regenerates from persisted picks instead of
    // overwriting them with (possibly stale/unhydrated) local selection state.
    void triggerGenerate(undefined, { regenerate: true });
  };

  const isRegenerating = isGenerating && didRequestRegenerate;

  // Regeneration only makes sense once an assessment with documents exists.
  const canRegenerate =
    !isGenerating &&
    !overDocumentLimit &&
    freshnessStatus !== 'no_documents' &&
    freshnessStatus !== 'needs_generation';
  const downloadDisabledReason = isDownloading
    ? 'The early case assessment is currently being exported.'
    : isDownloadDisabled
      ? 'Download is available after the early case assessment has been generated.'
      : null;
  const regenerateDisabledReason = isGenerating
    ? 'Early case assessment generation is already in progress.'
    : overDocumentLimit && documentLimit != null
      ? `Reduce the selection to ${documentLimit} documents or fewer before regenerating.`
      : freshnessStatus === 'no_documents'
        ? 'Add documents before regenerating the early case assessment.'
        : freshnessStatus === 'needs_generation'
          ? 'Generate the initial early case assessment before regenerating it.'
          : null;

  const statusLabel = (() => {
    switch (freshnessStatus) {
      case 'running':
        return 'Updating analysis\u2026';
      case 'pending':
        return 'Update queued \u2014 starting soon';
      case 'stale':
        return 'Out of date \u2014 new documents available';
      case 'needs_generation':
        return 'Not generated yet';
      case 'no_documents':
        return null;
      case 'up_to_date':
        return contributingDocumentCount != null
          ? `Up to date \u00b7 ${contributingDocumentCount} document${contributingDocumentCount === 1 ? '' : 's'}`
          : 'Up to date';
      default:
        return 'Status unavailable';
    }
  })();

  if (!slug) {
    return null;
  }

  return (
    <>
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-baseline justify-end gap-2">
          <Button onClick={() => setIsSelectorOpen(true)} variant="outline" size="sm">
            <FileText className="mr-2 h-4 w-4" />
            Select Documents
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0">
                <span className="sr-only">More actions</span>
                <Menu className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="space-y-1">
              <DropdownMenuItemWithTooltip
                onClick={onDownload}
                disabled={isDownloadDisabled}
                disabledReason={downloadDisabledReason}
                className="flex items-center gap-2 px-3 py-2"
              >
                <Download className="h-4 w-4" />
                {isDownloading ? 'Exporting\u2026' : 'Download'}
              </DropdownMenuItemWithTooltip>
              <DropdownMenuItemWithTooltip
                onClick={() => setIsRegenerateConfirmOpen(true)}
                disabled={!canRegenerate}
                disabledReason={regenerateDisabledReason}
                className="flex items-center gap-2 px-3 py-2"
              >
                <RefreshCw className={`h-4 w-4 ${isRegenerating ? 'animate-spin' : ''}`} />
                {isRegenerating ? 'Regenerating\u2026' : 'Regenerate'}
              </DropdownMenuItemWithTooltip>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {statusLabel && (
          <p className="pt-1 text-xs text-slate-500">
            {statusLabel}
          </p>
        )}
        {overDocumentLimit && documentLimit != null && (
          <p className="text-xs text-amber-700">
            {effectiveDocumentCount ?? '?'} documents exceed the {documentLimit}-document limit.
            Reduce your selection to regenerate.
          </p>
        )}
      </div>

      <EarlyCaseAssessmentDocumentSelectorDialog
        open={isSelectorOpen}
        onOpenChange={setIsSelectorOpen}
        matterSlug={slug}
        savedSelection={savedSelection}
        onSave={handleSaveSelection}
        contributingDocumentCount={contributingDocumentCount}
      />

      <Dialog open={isRegenerateConfirmOpen} onOpenChange={setIsRegenerateConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate early case assessment?</DialogTitle>
            <DialogDescription>
              This rebuilds the assessment from scratch, which may consume a
              significant number of tokens. Your existing
              assessment will be replaced once regeneration completes.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRegenerateConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="crimson" onClick={handleRegenerate}>
              Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
